import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { chime } from '../lib/chime';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Download, ImageDown, Store, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KINDS = [
  { key: 'all', label: 'All' },
  { key: 'sprite', label: 'Sprites' },
  { key: 'texture', label: 'Textures' },
  { key: 'model', label: '3D Concepts' },
];

export default function Marketplace() {
  const { isAuthenticated } = useAuth();
  const [assets, setAssets] = useState([]);
  const [kind, setKind] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/marketplace/assets?kind=${kind}`);
      setAssets(r.data?.assets || []);
    } catch (e) {
      toast.error('Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [kind]);

  const importAsset = async (id) => {
    if (!isAuthenticated) {
      toast.info('Please sign in to import');
      return;
    }
    try {
      await axios.post(`${API}/marketplace/assets/${id}/import`, {}, { withCredentials: true });
      toast.success('Imported to your library');
      chime('success');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    }
  };

  const buyAsset = async (id) => {
    if (!isAuthenticated) {
      toast.info('Please sign in to buy');
      return;
    }
    try {
      const r = await axios.post(`${API}/marketplace/assets/${id}/checkout`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (r.data?.free) {
        return importAsset(id);
      }
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="marketplace-page">
      <header className="border-b border-[#1E1E24] bg-[#0A0A0B]">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/" className="text-zinc-400 hover:text-white flex items-center gap-2 text-sm">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <div className="flex-1" />
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <Store className="w-4 h-4 text-[#06B6D4]" />
            <span className="font-mono text-xs">{assets.length} PACKS</span>
          </div>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 pt-10 pb-6">
        <h1 className="text-4xl sm:text-5xl font-black mb-2">Community Asset Marketplace</h1>
        <p className="text-sm text-zinc-400 max-w-2xl">
          Free RPG-grade sprites, textures and 3D concepts published by PlayMold creators.
          Anyone can browse — sign in to import a piece straight into your library.
        </p>

        <div className="flex flex-wrap gap-2 mt-6">
          {KINDS.map(k => (
            <button
              key={k.key}
              onClick={() => setKind(k.key)}
              className={`px-4 py-1.5 rounded-full text-xs font-mono uppercase border transition-colors
                ${kind === k.key ? 'bg-[#06B6D4] text-black border-[#06B6D4]' : 'border-[#1E1E24] text-zinc-400 hover:text-white'}`}
              data-testid={`marketplace-filter-${k.key}`}
            >
              {k.label}
            </button>
          ))}
        </div>
      </section>

      <main className="max-w-[1400px] mx-auto px-6 pb-16">
        {loading ? (
          <div className="py-16 text-center text-zinc-500 text-sm">Loading…</div>
        ) : assets.length === 0 ? (
          <div className="py-16 text-center border border-dashed border-[#1E1E24] rounded-lg" data-testid="marketplace-empty">
            <Store className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">No published assets yet. Be the first — publish one from your Assets library.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="marketplace-grid">
            {assets.map(a => (
              <article
                key={a.asset_id}
                className="bg-[#0A0A0C] border border-[#1E1E24] rounded-lg overflow-hidden group hover:border-[#06B6D4]/50 transition-colors"
                data-testid={`marketplace-card-${a.asset_id}`}
              >
                <div className="aspect-square bg-[#121214] relative overflow-hidden">
                  <img src={a.url} alt={a.prompt} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-3">
                  <p className="text-xs text-white line-clamp-2 mb-1" title={a.prompt}>{a.prompt}</p>
                  <div className="flex items-center justify-between text-[10px] font-mono text-zinc-500 mb-2">
                    <span>{(a.asset_type || 'asset').toUpperCase()}</span>
                    <span>by {a.author_name || 'anon'}</span>
                  </div>
                  <div className="flex gap-1">
                    {a.price_usd && a.price_usd > 0 ? (
                      <Button
                        onClick={() => buyAsset(a.asset_id)}
                        size="sm"
                        className="flex-1 h-7 text-xs bg-amber-500 text-black hover:bg-amber-400"
                        data-testid={`marketplace-buy-${a.asset_id}`}
                      >
                        <ImageDown className="w-3 h-3 mr-1" /> Buy ${a.price_usd.toFixed(2)}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => importAsset(a.asset_id)}
                        size="sm"
                        className="flex-1 h-7 text-xs bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90"
                        data-testid={`marketplace-import-${a.asset_id}`}
                      >
                        <ImageDown className="w-3 h-3 mr-1" /> Import free
                      </Button>
                    )}
                    <a
                      href={a.url}
                      target="_blank"
                      rel="noreferrer"
                      className="w-7 h-7 rounded-md border border-[#1E1E24] flex items-center justify-center text-zinc-400 hover:text-[#06B6D4]"
                      title="Open full-size"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
